import React, { useEffect, useState, useCallback } from 'react';
import {
  Edit2, Trash2, Plus, Loader, X, Save, Zap, Hash, DollarSign, Tag, ArrowUpDown,
} from 'lucide-react';
import api from '../../services/api';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import PageHeader from '../../components/PageHeader';

function PackModal({ pack, onClose, onSaved }) {
  const isEdit = !!pack;
  const [form, setForm] = useState(
    isEdit
      ? {
        credits: pack.credits ?? '',
        priceUsd: pack.priceUsd ?? '',
        label: pack.label || '',
        sortOrder: pack.sortOrder ?? 0,
        isActive: pack.isActive !== false,
      }
      : { credits: '', priceUsd: '', label: '', sortOrder: 0, isActive: true }
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (form.credits === '' || Number(form.credits) < 1) e.credits = 'Credits must be at least 1';
    if (form.priceUsd === '' || Number(form.priceUsd) < 0) e.priceUsd = 'Price cannot be negative';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const payload = { ...form, credits: Number(form.credits), priceUsd: Number(form.priceUsd), sortOrder: Number(form.sortOrder) || 0 };
    try {
      setSaving(true);
      if (isEdit) {
        await api.put(`/admin/api-credit-plans/${pack._id}`, payload);
        alertService.toast('success', 'Credit pack updated');
      } else {
        await api.post('/admin/api-credit-plans', payload);
        alertService.toast('success', 'Credit pack created');
      }
      onSaved();
    } catch (e) {
      alertService.error('Save Failed', e.response?.data?.error || 'Failed to save credit pack.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40" onClick={onClose}>
      <Card className="w-full max-w-lg p-0 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] border-[#E5E5E5] bg-white rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-4 sm:px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[#0176D3]">
              <Zap size={20} strokeWidth={2} />
            </div>
            <div className="space-y-1">
              <h2 className="text-[16px] font-bold text-[#2E2E2E]">{isEdit ? 'Edit Credit Pack' : 'Create Credit Pack'}</h2>
              <p className="text-[13px] text-[#706E6B]">{isEdit ? `Modifying: ${pack.credits.toLocaleString()} credits` : 'Define a new purchasable AI-credit pack'}</p>
            </div>
          </div>
          <button className="text-[#706E6B] hover:text-[#080707] hover:bg-[#F3F2F2] p-2 rounded-md transition-colors" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">Credits</label>
              <Input icon={Hash} type="number" min="1" value={form.credits} onChange={e => set('credits', e.target.value)} error={errors.credits} required />
            </div>
            <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">Price (USD)</label>
              <Input icon={DollarSign} type="number" min="0" step="0.01" value={form.priceUsd} onChange={e => set('priceUsd', e.target.value)} error={errors.priceUsd} required />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[12px] font-semibold text-[#444444]">Label (optional)</label>
            <Input icon={Tag} value={form.label} placeholder="e.g. Starter, Growth, Scale" onChange={e => set('label', e.target.value)} />
          </div>

          <div className="space-y-2">
            <label className="text-[12px] font-semibold text-[#444444]">Sort Order</label>
            <Input icon={ArrowUpDown} type="number" value={form.sortOrder} onChange={e => set('sortOrder', e.target.value)} hint="Lower numbers show first on the tenant Billing page." />
          </div>

          <label className="flex items-center justify-between p-4 rounded-lg bg-white border border-[#E5E5E5] hover:bg-[#F4F6F9] transition-all cursor-pointer group shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
            <div className="space-y-1">
              <span className="block text-[13px] font-bold text-[#080707] leading-none">Visible to tenants</span>
              <span className="block text-[12px] text-[#706E6B]">Inactive packs stay in history but no longer show up for purchase</span>
            </div>
            <div className={cn("w-10 h-5 rounded-full relative transition-all", form.isActive ? "bg-[#0176D3]" : "bg-[#D8DDE6]")}>
              <div className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow-sm", form.isActive ? "left-[22px]" : "left-0.5")} />
            </div>
            <input type="checkbox" className="hidden" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
          </label>
        </div>

        <div className="px-4 sm:px-6 py-3 border-t border-[#E5E5E5] flex justify-end gap-2 bg-white">
          <button className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded hover:bg-[#F3F2F2] transition-colors" onClick={onClose}>Cancel</button>
          <button
            className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            {isEdit ? 'Save Changes' : 'Create Pack'}
          </button>
        </div>
      </Card>
    </div>
  );
}

export default function SuperAdminApiCreditPlans() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | pack_object
  const [gateway, setGateway] = useState('stripe');
  const [savingGateway, setSavingGateway] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/api-credit-plans');
      setPacks(res.data.packs || []);
    } catch (e) {
      alertService.error('Connection Error', e.response?.data?.error || 'Failed to load credit plans.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/admin/settings')
      .then(res => setGateway(res.data.settings?.payment?.apiCreditsGateway || 'stripe'))
      .catch(() => {});
  }, []);

  // Which gateway handles AI-credit checkout — read by ApiCreditController.
  // checkout, same generic PlatformSettings 'payment' category the rest of
  // Platform Integrations already writes to (see admin.routes.js GET/PUT
  // /admin/settings). Razorpay needs its keys configured under Platform
  // Integrations first, or checkout will fail with a clear error.
  const saveGateway = async (value) => {
    setSavingGateway(true);
    try {
      await api.put('/admin/settings', { settings: { payment: { apiCreditsGateway: value } } });
      setGateway(value);
      alertService.toast('success', `AI credit checkout now uses ${value === 'razorpay' ? 'Razorpay' : 'Stripe'}`);
    } catch (e) {
      alertService.error('Save Failed', e.response?.data?.error || 'Failed to update the payment gateway.');
    } finally {
      setSavingGateway(false);
    }
  };

  // Packs are never permanently deleted — a past Stripe checkout or credit
  // transaction can still reference one, and deleting it would leave that
  // reference dangling. Deactivating hides it from the tenant Billing page
  // while keeping every historical purchase intact.
  const handleDeactivate = async (id, credits) => {
    const isConfirmed = await alertService.confirm(
      'Deactivate Pack?',
      `Deactivate the ${credits.toLocaleString()}-credit pack? It will no longer be offered to tenants, but past purchases are unaffected. Packs are never permanently deleted.`,
      'danger',
      'Deactivate'
    );
    if (!isConfirmed) return;
    try {
      await api.delete(`/admin/api-credit-plans/${id}`);
      alertService.toast('success', 'Pack deactivated');
      load();
    } catch (e) {
      alertService.error('Operation Failed', e.response?.data?.error || 'Failed to deactivate pack.');
    }
  };

  if (loading && packs.length === 0) return (
    <div className="w-full py-40 flex flex-col items-center justify-center gap-8 opacity-30">
      <Loader size={48} className="animate-spin text-[#0176D3]" />
      <div className="text-[11px] font-bold uppercase text-[#706E6B] animate-pulse">Loading Credit Plans…</div>
    </div>
  );

  return (
    <div className="w-full pb-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <PageHeader
        title="API Credit Plans"
        icon={Zap}
        subtitle={`The purchasable credit packs every tenant sees on Billing → API Credits. Prices are entered in USD; tenants see it converted into their own currency for display. Active packs: ${packs.filter(p => p.isActive).length}`}
        actions={<button className="h-8 px-3 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded inline-flex items-center gap-2 whitespace-nowrap" onClick={() => setModal('create')}><Plus size={14} /> New Pack</button>}
      />

      <div className="bg-white border border-[#E5E5E5] rounded-lg p-5 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="space-y-1">
          <h3 className="text-[14px] font-bold text-[#080707]">Checkout Payment Gateway</h3>
          <p className="text-[12px] text-[#706E6B] max-w-xl">
            Which gateway actually charges the tenant when they buy a credit pack. Razorpay charges in INR
            (converted from the USD price at the live rate); Stripe charges the USD price directly. Configure the
            chosen gateway's keys under Super Admin → Platform Integrations first.
          </p>
        </div>
        <select
          value={gateway}
          disabled={savingGateway}
          onChange={e => saveGateway(e.target.value)}
          className="h-10 px-4 bg-white border border-[#E5E5E5] rounded text-[13px] text-[#080707] outline-none focus:border-[#0176D3] disabled:opacity-50 whitespace-nowrap"
        >
          <option value="stripe">Stripe (USD)</option>
          <option value="razorpay">Razorpay (INR)</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {packs.map(pack => (
          <Card key={pack._id} padding="p-0" className={cn(
 "flex flex-col overflow-hidden relative transition-all duration-300 border-[#D8DDE6] bg-white rounded-md shadow-sm",
 !pack.isActive && "opacity-60"
 )}>
            {!pack.isActive && (
              <div className="absolute top-4 right-4 z-20">
                <Badge variant="rose" className="text-[11px] font-semibold">Inactive</Badge>
              </div>
            )}

            <div className="p-6 flex-1 flex flex-col gap-4">
              <div className="space-y-2">
                {pack.label && <p className="text-[12px] font-semibold text-[#0176D3] uppercase tracking-wide">{pack.label}</p>}
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-[#080707] tabular-nums leading-none">{pack.credits.toLocaleString()}</span>
                  <span className="text-[13px] text-[#706E6B]">credits</span>
                </div>
                <p className="text-[18px] font-bold text-[#080707]">${pack.priceUsd}</p>
              </div>
              <div className="text-[12px] text-[#706E6B] pt-2 border-t border-[#D8DDE6]">Sort order: {pack.sortOrder ?? 0}</div>
            </div>

            <div className="p-4 border-t border-[#D8DDE6] flex gap-3 bg-[#F4F6F9] mt-auto">
              <button
                className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded flex items-center justify-center gap-2 hover:bg-[#F3F2F2] transition-colors"
                onClick={() => setModal(pack)}
              >
                <Edit2 size={14} /> Edit
              </button>
              <button
                className="w-9 h-9 rounded-lg bg-white border border-[#E5E5E5] flex items-center justify-center text-[#706E6B] hover:text-[#BA0517] hover:bg-[#FEF1EE] hover:border-[#FDDDE3] transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                title="Deactivate" aria-label="Deactivate"
                onClick={() => handleDeactivate(pack._id, pack.credits)}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </Card>
        ))}

        <button
          onClick={() => setModal('create')}
          className="rounded-md border-2 border-dashed border-[#D8DDE6] bg-white hover:border-[#0176D3] hover:bg-[#F4F6F9] transition-all group flex flex-col items-center justify-center py-12 gap-4 min-h-[220px]"
        >
          <div className="w-12 h-12 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[#706E6B] group-hover:text-[#0176D3] group-hover:border-[#0176D3] transition-colors shadow-sm">
            <Plus size={24} strokeWidth={2} />
          </div>
          <div className="text-center space-y-1">
            <h3 className="text-[16px] font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors">Add New Pack</h3>
            <p className="text-[13px] text-[#706E6B]">Offer a new credit tier</p>
          </div>
        </button>
      </div>

      {modal && (
        <PackModal
          pack={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
